import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, History } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LegalContent } from "@/components/LegalContent";
import { btnGhost, btnPrimary, Field, inputCls, PageHead, Panel } from "./ui";

const fmt = (d) => new Date(d).toLocaleString("it-IT");

const Editor = ({ page, onSaved }) => {
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(false);
  const [versions, setVersions] = useState([]);
  const loadVersions = () => api.get(`/admin/legal/${page.slug}/versions`).then((r) => setVersions(r.data));
  useEffect(() => { setTitle(page.title); setContent(page.content); loadVersions(); }, [page.slug, page.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = title !== page.title || content !== page.content;

  const save = async () => {
    try { const { data } = await api.put(`/admin/legal/${page.slug}`, { title, content, note }); setNote(""); onSaved(data); toast.success(`Salvata versione ${data.version}`); } catch (e) { toast.error(apiError(e)); }
  };
  const restore = async (v) => {
    if (!window.confirm(`Ripristinare la versione ${v}? Verrà creata una nuova versione.`)) return;
    try { onSaved((await api.post(`/admin/legal/${page.slug}/restore/${v}`)).data); toast.success("Versione ripristinata"); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid xl:grid-cols-[1fr_300px] gap-5 items-start">
      <Panel testId="legal-editor" title={page.title} actions={
        <div className="flex gap-2">
          <button data-testid="legal-preview-toggle" onClick={() => setPreview(!preview)} className={btnGhost}>{preview ? "Modifica" : "Anteprima"}</button>
          <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className={btnGhost}><ExternalLink className="w-4 h-4" /></a>
        </div>
      }>
        <div className="space-y-4">
          <Field label="Titolo"><input data-testid="legal-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} /></Field>
          {preview ? <div className="border border-[#D5B98C]/50 p-4 bg-[#F5F0E8]"><LegalContent content={content} /></div> : (
            <Field label="Testo" hint="## Titolo sezione · - elenco · **grassetto** · [testo](/pagina) · [[DA VERIFICARE: nota]] segna una parte provvisoria">
              <textarea data-testid="legal-content-input" value={content} onChange={(e) => setContent(e.target.value)} rows={24} className={`${inputCls} font-mono text-[13px] leading-relaxed`} />
            </Field>
          )}
          <Field label="Nota della modifica (facoltativa)"><input data-testid="legal-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} /></Field>
          <button data-testid="legal-save" disabled={!dirty} onClick={save} className={btnPrimary}>Salva nuova versione</button>
        </div>
      </Panel>
      <Panel title="Versioni" testId="legal-versions" actions={<History className="w-4 h-4 text-[#956842]" />}>
        <ul className="space-y-2 text-sm max-h-[520px] overflow-y-auto">
          {versions.map((v) => (
            <li key={v.version} data-testid={`legal-version-${v.version}`} className="border-b border-[#D5B98C]/40 pb-2">
              <div className="flex justify-between gap-2"><b>v{v.version}</b>{v.version !== page.version ? <button data-testid={`legal-restore-${v.version}`} onClick={() => restore(v.version)} className="text-xs underline">Ripristina</button> : <span className="text-[10px] uppercase tracking-widest text-[#3d5a3a]">Attuale</span>}</div>
              <div className="text-[11px] text-[#242321]/55">{fmt(v.updated_at)} · {v.updated_by}</div>
              {v.note && <div className="text-xs mt-0.5">{v.note}</div>}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};

export default function AdminLegal() {
  const { isOwner } = useAuth();
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { if (isOwner) api.get("/legal").then((r) => { setPages(r.data); setActive(r.data[0]?.slug); }); }, [isOwner]);
  if (!isOwner) return <Navigate to="/admin" replace />;
  const page = pages.find((p) => p.slug === active);
  const onSaved = (d) => setPages((ps) => ps.map((p) => (p.slug === d.slug ? d : p)));

  return (
    <div data-testid="admin-legal">
      <PageHead title="Pagine legali" subtitle="Ogni salvataggio crea una nuova versione con data. Le parti 'Da verificare' sono mostrate come provvisorie sul sito." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
        {pages.map((p) => (
          <button key={p.slug} data-testid={`legal-tab-${p.slug}`} onClick={() => setActive(p.slug)}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${active === p.slug ? "bg-[#39251D] text-[#F5F0E8] border-[#39251D]" : "border-[#D5B98C] hover:border-[#39251D]"}`}>
            {p.incomplete ? <AlertTriangle className="w-3.5 h-3.5 text-[#D5B98C]" /> : <CheckCircle2 className="w-3.5 h-3.5 text-[#3d5a3a]" />}
            {p.title}{p.incomplete && <span className="text-[10px] opacity-75">({p.to_verify})</span>}
          </button>
        ))}
      </div>
      {page && <Editor page={page} onSaved={onSaved} />}
    </div>
  );
}
